import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ByWeekday, RRule, rrulestr, Weekday } from 'rrule';
import { ScheduleAvailabilityTypes } from '@frontend/api-schedule-availability';
import { formatDate } from '@frontend/date';
import { DaysOfWeek } from '@frontend/design-system';
import {
  AvailabilityRecurrenceTypeOptions,
  AvailabilityRecurrenceTypes,
  MonthlyFrequencyTypeOptions,
  MonthlyRecurrenceFormValues,
  MonthlyWeekDayOptions,
  RepeatFrequencyOptions,
  RuleRecurrenceFormValues,
} from './components/availability/availability-form/types';
import { ScheduleHours, ScheduleHoursSessionMap } from './types';
import { getTimeDiff } from './utils/date-helpers';

dayjs.extend(utc);

const dayMap = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
} as const;

export type DayMapKey = keyof typeof dayMap;

const daysMap = {
  monday: 'MO',
  tuesday: 'TU',
  wednesday: 'WE',
  thursday: 'TH',
  friday: 'FR',
  saturday: 'SA',
  sunday: 'SU',
} as const;

type DaysMapKey = keyof typeof daysMap;

export const daysStringMap = {
  MO: 'monday',
  TU: 'tuesday',
  WE: 'wednesday',
  TH: 'thursday',
  FR: 'friday',
  SA: 'saturday',
  SU: 'sunday',
} as const;

const daysNumberMap = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

export const weekNumberString: Record<number, 'first' | 'second' | 'third' | 'fourth' | 'last'> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  [-1]: 'last',
} as const;

const isDayAbbreviated = (day: DaysMapKey | DayMapKey): day is DayMapKey => {
  return Object.keys(dayMap).includes(day);
};

/**
 * @desc: Function is defined to get the rrule day of the week (Weekday) based on the day abbreviation
 * @param day : string | number - day abbreviation or day number (0 for Sunday, 1 for Monday, ..., 6 for Saturday)
 * @returns : Weekday
 */
export const getRRuleDay = (day: number | DaysMapKey | DayMapKey): Weekday => {
  const dayAbbrString = typeof day === 'number' ? daysNumberMap[day] : isDayAbbreviated(day) ? day : daysMap[day];
  return (dayAbbrString && dayMap[dayAbbrString]) || RRule.MO;
};

/**
 * @desc: Function is defined to create the rrule object based on the session hours details
 * @param sessionHoursDetails
 * @returns : Object : { rrule: string, duration: number }
 */
const curriedBuildRRule = (timezone: string) => (sessionHoursDetails: ScheduleHoursSessionMap) => {
  const { days, startTime, duration } = sessionHoursDetails;

  const rruleObj = new RRule({
    freq: RRule.WEEKLY,
    byweekday: days.map((day) => getRRuleDay(day as DayMapKey)),
    dtstart: dayjs.utc(startTime, 'HH:mm').toDate(),
    tzid: timezone,
  });

  return { rrule: rruleObj.toString(), duration };
};
/**
 * @desc: Function is defined to convert the schedule hours object to rrule object for working hours and breaks
 * @param schedule : ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek
 * @param timezone : string
 * @returns ScheduleHours
 */
export const convertScheduleHoursObjectToRRuleObject = (
  schedule: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek,
  timezone?: string
): ScheduleHours => {
  const processSchedule = (
    scheduleMap: Map<string, ScheduleHoursSessionMap>,
    dayAbbreviation: string,
    startTime: string,
    endTime: string,
    type: 'working_hours' | 'break_hours'
  ) => {
    const duration = getTimeDiff(startTime, endTime, { format: 'HH:mm' });
    const key = `${startTime}-${duration}`;
    const session = scheduleMap.get(key) || {
      days: [],
      startTime,
      endTime,
      duration,
      type,
    };
    session.days.push(dayAbbreviation);
    scheduleMap.set(key, session);
  };

  const workingHoursMap = new Map<string, ScheduleHoursSessionMap>();
  const breaksMap = new Map<string, ScheduleHoursSessionMap>();

  for (const [day, schedules] of Object.entries(schedule)) {
    const dayAbbreviation = daysMap[day as DaysMapKey];
    schedules.forEach((schedule) => {
      processSchedule(workingHoursMap, dayAbbreviation, schedule.startTime, schedule.endTime, 'working_hours');
      schedule.breaks?.forEach((breakItem) => {
        processSchedule(breaksMap, dayAbbreviation, breakItem.startTime, breakItem.endTime, 'break_hours');
      });
    });
  }

  const buildRRule = curriedBuildRRule(timezone ?? '');

  const workingHours = Array.from(workingHoursMap.values()).map(buildRRule);
  const breaks = Array.from(breaksMap.values()).map(buildRRule);

  return { workingHours, breaks };
};
/**
 * @desc: This method is used to convert the schedule hours from rrule format to schedule hours object
 * @param scheduleHoursInRRule: ScheduleHours
 * @returns: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek
 */
export const convertFromRRuleScheduleHoursToScheduleHoursObject = (
  scheduleHoursInRRule: ScheduleHours
): ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek => {
  const output: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek = {};

  const processRRule = (item: { rrule?: string; duration?: number }, type: 'working_hours' | 'break_hours') => {
    const { startTime, endTime, daysOfWeek } = getScheduleInfoFromRRule(item.rrule || '', item.duration || 0, 'HH:mm');

    daysOfWeek.forEach((day) => {
      const dayName = dayjs().day(day).format('dddd').toLowerCase() as ScheduleAvailabilityTypes.Weekday;
      output[dayName] = output[dayName] || [];

      if (type === 'working_hours') {
        output[dayName].push({ startTime, endTime });
      } else {
        output[dayName][0].breaks = output[dayName][0].breaks || [];
        output[dayName][0].breaks.push({ startTime, endTime });
      }
    });
  };

  // Process working hours
  scheduleHoursInRRule?.workingHours?.forEach((item) => processRRule(item, 'working_hours'));

  // Process breaks
  scheduleHoursInRRule?.breaks?.forEach((item) => processRRule(item, 'break_hours'));

  return output;
};

/**
 * @desc: Function to extract the date range from an RRule string
 * @param rRuleString : string - The RRule string
 * @returns : Object : { startDatetime?: Date; endDatetime?: Date }
 */
export const getDateRangeFromRRule = (
  rRuleString?: string
): { startDateTime?: string; endDateTime?: string; tzid?: string | null } => {
  if (!rRuleString) return {};

  const rRuleObj = rrulestr(rRuleString);
  const { dtstart, count } = rRuleObj.options;

  return {
    startDateTime: dayjs.utc(dtstart).toISOString(),
    endDateTime: count
      ? dayjs
          .utc(dtstart)
          .add(count - 1, 'days')
          .toISOString()
      : undefined,
    tzid: rRuleObj.options.tzid,
  };
};

interface IScheduleExceptionInfo {
  startDate?: string;
  endDate?: string;
  startTime: string;
}

/**
 * @desc: Function to generate an RRule string from a given date range and time range
 * @param dateRange : [string, string] - The start and end dates in 'MM/DD/YYYY' format
 * @param timeRange : [string, string] - The start and end times in 'HH:mm:ss' format
 * @returns : string - The generated RRule string
 */
export const getRRuleStringFromDateRange = (
  { startDate, endDate, startTime }: IScheduleExceptionInfo,
  timezone: string
): string => {
  // Use startDate if endDate is missing and vice versa
  const effectiveStartDate = startDate || endDate;
  const effectiveEndDate = endDate || startDate;

  if (!effectiveStartDate || !effectiveEndDate) {
    throw new Error('Both startDate and endDate cannot be empty');
  }

  const dtstart = dayjs.utc(`${effectiveStartDate} ${startTime}`, 'MM/DD/YYYY HH:mm:ss').toDate();
  const count = dayjs(effectiveEndDate).diff(dayjs(effectiveStartDate), 'days') + 1;

  return new RRule({ freq: RRule.DAILY, dtstart, count, tzid: timezone }).toString();
};

/**
 * @desc: Interface representing schedule information extracted from an RRule string
 */
interface IScheduleInfo {
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
}

/**
 * Extracts schedule information from an RRule string.
 *
 * @param {string} rrule - The recurrence rule string.
 * @param {number} [duration=0] - The duration in minutes for the event.
 * @param {'HH:mm' | 'hh:mm A'} timeFormat - The format for the start and end times.
 * @returns {IScheduleInfo} An object containing the start time, end time, and days of the week.
 */
export const getScheduleInfoFromRRule = (
  rrule: string,
  duration = 0,
  timeFormat: 'HH:mm' | 'hh:mm A'
): IScheduleInfo => {
  const rule = RRule.fromString(rrule);

  const dtStart = rule.options.dtstart;
  const startTime = formatDate(dtStart, timeFormat, true);
  const endTime = startTime ? dayjs(startTime, timeFormat).add(duration, 'm').format(timeFormat) : '';

  // rrule - MO: 0, TU: 1, WE: 2, TH: 3, FR: 4, SA: 5, SU: 6
  // dayjs - MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 0,

  // Convert RRule weekdays to dayjs weekdays
  const daysOfWeek = rule.options.byweekday.map((weekday: number) => (weekday === 6 ? 0 : weekday + 1));

  return {
    startTime,
    endTime,
    daysOfWeek,
  };
};

export const getRRuleTimeString = (time: string | undefined): string => {
  const [hours = '00', minutes = '00', seconds = '00'] = (time ?? '').split(':');
  return `${hours.padStart(2, '0')}${minutes.padStart(2, '0')}${seconds.padStart(2, '0')}`;
};

export const getRRuleDateString = (date: string | undefined): string => {
  return date ? dayjs(date).format('YYYYMMDD') : '';
};

export const formRRuleFromAvailabilityRuleFormValues = ({
  isAvailabilityRule,
  recurrenceType,
  recurrenceFormValues,
  monthlyRecurrenceFormValues,
  daysOfWeek,
  repeatOn,
}: {
  isAvailabilityRule?: boolean;
  recurrenceType: AvailabilityRecurrenceTypes | undefined;
  recurrenceFormValues: RuleRecurrenceFormValues;
  monthlyRecurrenceFormValues: MonthlyRecurrenceFormValues;
  daysOfWeek: Weekday[];
  repeatOn: Weekday[];
}) => {
  const { repeatFrequency, repeatFrequencyType } = recurrenceFormValues;

  if (recurrenceType === 'SPECIFIC_DATE') {
    return new RRule({
      freq: RRule.YEARLY,
    });
  }

  if (recurrenceType === 'WEEKLY') {
    return new RRule({
      freq: RRule.WEEKLY,
      byweekday: daysOfWeek,
    });
  }

  if (recurrenceType === 'CUSTOM' && repeatFrequency) {
    const customRRuleOptions = { interval: Number(repeatFrequency) };
    if (repeatFrequencyType === 'days') {
      return new RRule({
        freq: RRule.DAILY,
        ...customRRuleOptions,
      });
    } else if (repeatFrequencyType === 'weeks') {
      return new RRule({
        freq: RRule.WEEKLY,
        byweekday: repeatOn,
        ...customRRuleOptions,
      });
    } else if (repeatFrequencyType === 'months') {
      const { monthlyFrequencyType, monthlyFrequencyDay, monthlyFrequencyWeek, monthlyFrequencyWeekday } =
        monthlyRecurrenceFormValues;
      if (monthlyFrequencyType === 'day') {
        return new RRule({
          freq: RRule.MONTHLY,
          bymonthday: Number(monthlyFrequencyDay),
          ...customRRuleOptions,
        });
      } else if (monthlyFrequencyType === 'weekday') {
        if (!monthlyFrequencyWeekday || !monthlyFrequencyWeek) return;
        const weekMap: Record<string, number> = {
          first: 1,
          second: 2,
          third: 3,
          fourth: 4,
          last: -1,
        };
        const rruleWeekday = getRRuleDay(monthlyFrequencyWeekday as DaysMapKey).nth(weekMap[monthlyFrequencyWeek]);
        return new RRule({
          freq: RRule.MONTHLY,
          byweekday: rruleWeekday,
          ...customRRuleOptions,
        });
      }
    }
  }

  // NOTE: Currently, Availability Rule Create/Update API doesn't support empty rRule, so we are setting single occurrence rRule.
  // This can be removed once API is updated to support empty rRule field.
  if (isAvailabilityRule && recurrenceType === 'SINGLE_OCCURRENCE') {
    return new RRule({
      freq: RRule.YEARLY,
      count: 1,
    });
  }

  return;
};

export const getAvailabilityRecurrenceTypeFromRRule = (
  rRuleObj: RRule
): AvailabilityRecurrenceTypeOptions | undefined => {
  const options = rRuleObj.options;
  if (options.freq === RRule.YEARLY && options.count === 1) {
    // SINGLE_OCCURRENCE
    return;
  }
  if (options.freq === RRule.YEARLY && !options.byweekday && !options.bymonthday) {
    return 'SPECIFIC_DATE';
  }
  if (options.freq === RRule.WEEKLY && options.byweekday && !options.interval) {
    return 'WEEKLY';
  }
  if (
    options.freq === RRule.DAILY ||
    options.freq === RRule.MONTHLY ||
    (options.freq === RRule.WEEKLY && options.interval)
  ) {
    return 'CUSTOM';
  }
  return undefined;
};

export const getFrequencyValuesFromRRule = (rRuleObj: RRule | undefined) => {
  const { origOptions: rRuleOptions } = rRuleObj || {};
  const { freq, interval = 1, bymonthday, byweekday } = rRuleOptions || {};
  const frequency = interval?.toString();
  const frequencyType: RepeatFrequencyOptions =
    freq === RRule.WEEKLY ? 'weeks' : freq === RRule.MONTHLY ? 'months' : 'days';

  let monthlyFrequencyType: MonthlyFrequencyTypeOptions = 'day';
  let monthlyFrequencyDay = '1';
  let monthlyFrequencyWeek = 'first';
  let monthlyFrequencyWeekday: MonthlyWeekDayOptions = 'monday';
  const weekdays: Weekday[] = [];

  if (frequencyType === 'weeks') {
    const byweekdays = Array.isArray(byweekday) ? byweekday : byweekday ? [byweekday] : [];
    weekdays.push(
      ...byweekdays.map((day) => getWeekdayFromByWeekDay(day)[0]).filter((day): day is Weekday => day !== undefined)
    );
  } else if (frequencyType === 'months') {
    if (bymonthday) {
      const monthDay = Array.isArray(bymonthday) ? bymonthday[0] : bymonthday;
      monthlyFrequencyDay = monthDay.toString();
    } else if (byweekday) {
      monthlyFrequencyType = 'weekday';
      const firstByWeekday = Array.isArray(byweekday) ? byweekday[0] : byweekday;
      const [weekday, weekNumber] = getWeekdayFromByWeekDay(firstByWeekday);
      monthlyFrequencyWeekday = weekday ? daysStringMap[weekday.toString() as DayMapKey] : 'monday';
      monthlyFrequencyWeek = weekNumber !== undefined ? weekNumberString[weekNumber as number] : 'first';
    }
  }

  return {
    frequency,
    frequencyType,
    monthlyFrequencyType,
    monthlyFrequencyDay,
    monthlyFrequencyWeek,
    monthlyFrequencyWeekday,
    weekdays,
  };
};

export const getWeekdayFromByWeekDay = (
  byweekday: ByWeekday | undefined
): [Weekday | undefined, number | undefined] => {
  if (!byweekday) return [undefined, undefined];
  if (byweekday && typeof byweekday === 'object') {
    const weekdayIndex = byweekday.weekday; // 0..6 => MO..SU
    const weekNumber = byweekday.n; // 1,2,3,4,-1 (or undefined for plain weekly MO/WE)
    return [dayMap[daysNumberMap[weekdayIndex]], weekNumber];
  }
  if (typeof byweekday === 'number') {
    return [dayMap[daysNumberMap[byweekday]], undefined];
  }
  if (typeof byweekday === 'string') {
    const numString = Number(byweekday.slice(0, -2));
    const dayString = byweekday.slice(-2) as DayMapKey;
    return [dayMap[dayString], numString ? numString : undefined];
  }
  return [undefined, undefined]; // Default fallback
};

export const getRRuleDayFromDaysOfWeek = (day: DaysOfWeek): Weekday => getRRuleDay(day.toLowerCase() as DaysMapKey);

export const getDaysOfWeekFromRRuleDay = (rruleDay: Weekday): DaysOfWeek => {
  const dayString = daysStringMap[rruleDay.toString() as DayMapKey];
  return (dayString.charAt(0).toUpperCase() + dayString.slice(1)) as DaysOfWeek;
};
